// Live connectivity state, derived from what requests actually DO.
//
// `navigator.onLine` — the app's only signal until now — reports whether the
// machine has a network link, nothing more. It stays `true` through every
// failure users actually hit here: a laptop waking with dead sockets after an
// idle spell, a Wi-Fi roam, a VPN drop, a captive portal, the API host being
// unreachable. So the offline bar never appeared for the case that matters —
// requests failing (or hanging forever) while the browser insists all is
// well — and the page just sat on a spinner until someone hard-refreshed.
//
// Instead, every request reports its outcome here (see api.ts) and a two-stage
// reachability probe settles the ambiguous cases:
//   - a network-class failure, or a request still unsettled after SLOW_REQUEST_MS,
//     triggers a probe (deduped and briefly cached, so a burst costs one);
//   - a probe that gets a READABLE non-5xx answer means all is well → `ok`;
//   - a readable 5xx, or an answer we can't read the status of, means the host is
//     up but the services are not → `unavailable`;
//   - nothing answering at all means the connection is really gone → `lost`.
//     Either way we keep probing on a backoff until it comes back → `restored`.
//
// That middle case is the one this module exists to name. Our gateway sends 5xx
// WITHOUT CORS headers, so a 503 reaches JS as an indistinguishable "network
// error" with no readable status — the browser shows 503 in the Network tab while
// the app sees only a transport failure. Conflating the two meant the retry ladder
// used the fast pacing meant for stale sockets (three attempts inside five
// seconds, nowhere near long enough for services that take minutes to boot) and
// the UI fell through to the full-screen error page instead of the bar.
//
// This module owns no UI. App.tsx subscribes and renders the bar.

import { captureError } from 'src/common/analytics';
import {
  beginOutage,
  endOutage,
  logError,
  logWarn,
  setLogConnectionState
} from 'src/common/frontendLog';

export type ConnectionState =
  /** Requests are getting through (or nothing says otherwise yet). */
  | 'ok'
  /**
   * The machine has no network link at all (`navigator.onLine === false`). Its own
   * state rather than a flavour of `lost` because it is the one case where the
   * problem is on the user's side and the bar should say so — and because it can
   * start or stop mid-outage, which only a state change makes the UI notice.
   */
  | 'offline'
  /**
   * Nothing answers, but the machine does have a link — the API host is down,
   * DNS is failing, a VPN dropped. Not the user's fault, so it gets the
   * server-side wording.
   */
  | 'lost'
  /**
   * The host IS answering, but with 5xx — the services are down or still
   * powering on. Indistinguishable from `lost` to the user (nothing works, wait
   * it out) so it raises the same bar, but it is a different fact and it recovers
   * differently: only a readable non-5xx answer clears it, because an opaque
   * probe resolves happily against a 503.
   */
  | 'unavailable'
  /** Just came back; the bar says so briefly, then hides. */
  | 'restored';

/**
 * What a probe actually found. The distinction between the last two is the one
 * that matters and the one a plain XHR cannot make: a gateway 5xx sent without
 * CORS headers reaches JS as an indistinguishable "network error", so the app
 * used to treat "the services are booting" exactly like "the wire is dead" — and
 * picked the wrong retry pacing and the wrong UI as a result.
 */
export type Reachability =
  /** Answered, and not with a 5xx. */
  | 'available'
  /** Answered, but the service is not usable (5xx, or an unreadable error). */
  | 'unavailable'
  /** Nothing answered at all. */
  | 'unreachable';

/**
 * First re-probe after we conclude the connection is down. Deliberately short:
 * this is also the floor of the backoff, so it sets how long a user waits to be
 * told the connection came back. At 4s the bar felt sluggish to the point of
 * looking broken.
 */
const FIRST_PROBE_MS = 1200;
/** Probe interval ceiling — back off so a long outage isn't a request storm. */
const MAX_PROBE_MS = 20000;
/** A probe that hasn't answered by now counts as unreachable. */
const PROBE_TIMEOUT_MS = 4500;
/**
 * The moment the browser says the link is back is the moment its DNS and routing
 * are least likely to be ready, so the first probe after `online` very often
 * fails through no fault of the server. Retry a few times across ~4s before
 * publishing any verdict — otherwise a reconnect flashes the bar to "can't reach
 * the server" on its way to going green, which reads as a new problem rather than
 * a recovery.
 */
const RECONNECT_PROBE_DELAYS_MS = [0, 400, 1200, 2500];
/** How long "back online" stays up before the bar hides itself. */
const RESTORED_VISIBLE_MS = 6000;

type Listener = (state: ConnectionState) => void;

let state: ConnectionState = 'ok';
let consecutiveFailures = 0;
let probeUrl: string | null = null;
let probeInFlight = false;
let probeDelay = FIRST_PROBE_MS;
let probeTimer: ReturnType<typeof setTimeout> | undefined;
let restoredTimer: ReturnType<typeof setTimeout> | undefined;

const listeners = new Set<Listener>();

/**
 * Where to aim the reachability probe. Set by api.ts at import time so this
 * module stays free of any dependency on it (and of the base-URL constant).
 */
export function setProbeUrl(url: string): void {
  probeUrl = url;
}

export function getConnectionState(): ConnectionState {
  return state;
}

/** Subscribe to changes. Fires immediately with the current state. */
export function subscribeConnection(fn: Listener): () => void {
  listeners.add(fn);
  fn(state);
  return () => {
    listeners.delete(fn);
  };
}

function publish(next: ConnectionState) {
  if (state === next) return;
  state = next;
  // Keep the activity log's context field in step, so every entry written from
  // anywhere in the app carries the connection state as it was at that moment.
  setLogConnectionState(next);
  listeners.forEach((fn) => {
    try {
      fn(next);
    } catch (e) {
      captureError('common/connection:publish', e);
    }
  });
}

/** True when the machine reports no network link at all. */
const browserOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

/** Every state in which the bar is up and we are still probing for recovery. */
const isDown = (s: ConnectionState) =>
  s === 'offline' || s === 'lost' || s === 'unavailable';

/**
 * Nothing is answering. Splits into 'offline' (no link — the user's own
 * connection) and 'lost' (link present, but the API host isn't answering), because
 * those need different things said to the user: one is "check your internet", the
 * other is "the server isn't responding, not your fault".
 *
 * All the existing callers funnel through here, so the distinction is made in one
 * place — including the `offline` window event, which means a Wi-Fi drop DURING an
 * outage moves 'lost' → 'offline' and the bar re-words itself.
 */
function markLost() {
  if (restoredTimer) clearTimeout(restoredTimer);
  const wasDown = isDown(state);
  // Open the outage window BEFORE publishing, so the state-change entry below —
  // and everything logged until the connection returns — carries its id and the
  // whole incident can be pulled out of the file as one unit.
  const outageId = beginOutage();
  publish(browserOffline() ? 'offline' : 'lost');
  if (!wasDown) {
    // The API host is unreachable. This is the entry the server can't write,
    // because by definition nothing is arriving there to write it.
    logError('connection_lost', {
      msg: browserOffline()
        ? 'The machine has no network link — nothing can be sent'
        : 'API host is unreachable — requests are not landing on the server',
      reason: browserOffline() ? 'browser_offline' : 'probe_failed',
      outageId,
      extra: {
        consecutiveFailures,
        probeHost: probeHost(),
        // navigator.onLine is recorded precisely because it is usually `true`
        // here — that disagreement is the reason this module exists.
        navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined
      }
    });
  }
  // Reset the backoff when the outage starts, so recovery is noticed quickly.
  if (!wasDown) probeDelay = FIRST_PROBE_MS;
  scheduleProbe();
}

/**
 * The host answered, but with a 5xx — services down or still coming up. Same bar
 * as `lost` (from the user's seat both mean "nothing works, hold on"), but kept
 * as its own state because the retry pacing and the recovery test differ.
 */
function markUnavailable() {
  if (restoredTimer) clearTimeout(restoredTimer);
  const wasDown = isDown(state);
  const outageId = beginOutage();
  publish('unavailable');
  if (!wasDown) {
    logError('service_unavailable', {
      msg: 'API host is answering but the services are not available (5xx)',
      reason: 'server_5xx',
      outageId,
      extra: { consecutiveFailures, probeHost: probeHost() }
    });
  }
  if (!wasDown) probeDelay = FIRST_PROBE_MS;
  scheduleProbe();
}

function markReachable() {
  consecutiveFailures = 0;
  if (probeTimer) clearTimeout(probeTimer);
  probeDelay = FIRST_PROBE_MS;
  if (!isDown(state)) return;
  const closed = endOutage();
  // Closes the incident with the two numbers that matter after the fact: how
  // long the services were unreachable, and how many of the user's requests
  // died in that window.
  //
  // Recovery is good news, so `warn` looks odd — but the log now keeps only
  // failures, and an outage with a `connection_lost` and no matching restore
  // reads as "still broken, or we stopped watching". The resolution belongs with
  // the incident it resolves.
  logWarn('connection_restored', {
    msg: closed
      ? `API reachable again after ${Math.round(closed.durationMs / 1000)}s`
      : 'API reachable again',
    reason: 'probe_succeeded',
    outageId: closed?.id,
    durationMs: closed?.durationMs,
    extra: { failedRequests: closed?.failures, probeHost: probeHost() }
  });
  publish('restored');
  restoredTimer = setTimeout(() => publish('ok'), RESTORED_VISIBLE_MS);
}

/** Host only — the probe URL carries a cache-busting query we don't need logged. */
function probeHost(): string | undefined {
  if (!probeUrl) return undefined;
  try {
    return new URL(probeUrl).host;
  } catch {
    return probeUrl;
  }
}

function scheduleProbe() {
  if (probeTimer) clearTimeout(probeTimer);
  probeTimer = setTimeout(() => {
    probeDelay = Math.min(probeDelay * 2, MAX_PROBE_MS);
    void probe();
  }, probeDelay);
}

/** One probe request, with the shared timeout. */
async function probeFetch(mode: 'cors' | 'no-cors'): Promise<Response> {
  const controller = new AbortController();
  const abort = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const sep = (probeUrl as string).includes('?') ? '&' : '?';
    return await fetch(`${probeUrl}${sep}_probe=${Date.now()}`, {
      method: 'GET',
      mode,
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal
    });
  } finally {
    clearTimeout(abort);
  }
}

/**
 * Two stages, because one is not enough to answer the question that matters.
 *
 * 1. A normal `cors` request. If it resolves we can READ the status, which is the
 *    only way to distinguish "the services are 5xx-ing while they boot" from
 *    "everything is fine". A 4xx counts as available: the server is answering
 *    properly and an auth or not-found reply is not an outage.
 * 2. If that rejects we still don't know why — it is either a CORS-stripped 5xx
 *    (our gateway does not put Access-Control-Allow-Origin on error responses, so
 *    the browser hides the status from JS) or a genuinely dead wire. A `no-cors`
 *    retry settles it: an opaque response resolves for ANY answer, so if
 *    something came back the server is there and erroring; if this rejects too,
 *    nothing is answering.
 *
 * Stage 2 is what the app used to do alone, which is why a booting service looked
 * identical to a dropped connection.
 */
async function classify(): Promise<Reachability> {
  // Both requests go out together. Run one after the other and a black-holed
  // network — where each attempt hangs until its own abort rather than failing
  // fast — costs two full timeouts before we can say anything, which was most of
  // the several seconds the bar took to react. In parallel the worst case is one
  // timeout. The readable attempt is still what decides whenever it answers; the
  // opaque one is only consulted if the readable one could not be read at all, so
  // it costs a spare request per probe and buys back half the latency.
  const readable = probeFetch('cors').then(
    (res) => res.status,
    () => null
  );
  const opaque = probeFetch('no-cors').then(
    () => true,
    () => false
  );

  const status = await readable;
  if (status !== null) return status >= 500 ? 'unavailable' : 'available';
  // Unreadable. Did ANYTHING answer?
  return (await opaque) ? 'unavailable' : 'unreachable';
}

/** In-flight probe, so a burst of failures costs one probe rather than one each. */
let probePromise: Promise<Reachability> | null = null;
/** Last verdict, valid this briefly — long enough to cover one retry ladder step. */
let lastVerdict: { value: Reachability; at: number } | null = null;
const VERDICT_TTL_MS = 3000;

/** Apply a verdict to the published state. */
function applyVerdict(verdict: Reachability) {
  if (verdict === 'available') markReachable();
  else if (verdict === 'unavailable') markUnavailable();
  else markLost();
}

async function probe(): Promise<Reachability> {
  if (!probeUrl || typeof fetch !== 'function') {
    // api.ts sets the probe URL at ITS import time, which can be after ours (this
    // module is now also pulled in by state/globalstate). Without rescheduling, a
    // probe that lands in that window would silently end the retry loop and leave
    // the bar stuck up with nothing left to take it down.
    if (isDown(state)) scheduleProbe();
    return 'available';
  }
  // A reconnect sequence is mid-flight and owns the verdict for now: let it finish
  // rather than let a stray failing request publish 'lost' underneath it, which is
  // the flash we are trying to remove.
  if (reconnecting) return lastVerdict?.value ?? 'unreachable';
  if (probePromise) return probePromise;
  probeInFlight = true;
  probePromise = classify()
    .then((verdict) => {
      lastVerdict = { value: verdict, at: Date.now() };
      applyVerdict(verdict);
      return verdict;
    })
    .finally(() => {
      probeInFlight = false;
      probePromise = null;
    });
  return probePromise;
}

/** Guards the reconnect sequence so only one runs and nothing races it. */
let reconnecting = false;

/**
 * The browser says the link is back. Probe repeatedly and publish only the last
 * word — see RECONNECT_PROBE_DELAYS_MS for why the first attempt is untrustworthy.
 * A success at any point ends it early and goes straight to the green bar.
 */
async function probeAfterReconnect(): Promise<void> {
  if (reconnecting) return;
  reconnecting = true;
  if (probeTimer) clearTimeout(probeTimer);
  // The outage may have backed the interval off to 20s. Recovery is the moment we
  // most want to be quick, so start over from the floor.
  probeDelay = FIRST_PROBE_MS;
  try {
    for (let i = 0; i < RECONNECT_PROBE_DELAYS_MS.length; i++) {
      const wait = RECONNECT_PROBE_DELAYS_MS[i];
      if (wait) await new Promise((resolve) => setTimeout(resolve, wait));
      if (!probeUrl || typeof fetch !== 'function') return;
      const verdict = await classify();
      lastVerdict = { value: verdict, at: Date.now() };
      if (verdict === 'available') {
        markReachable();
        return;
      }
      // Only the final attempt is allowed to put the bar into a down state.
      if (i === RECONNECT_PROBE_DELAYS_MS.length - 1) applyVerdict(verdict);
    }
  } finally {
    reconnecting = false;
    // Genuinely still down — hand back to the normal backoff loop.
    if (isDown(state)) scheduleProbe();
  }
}

/**
 * The verdict, for a caller that needs to make a decision from it — currently the
 * retry ladder in common/api, which must know whether to wait seconds (a stale
 * socket recovers immediately) or half a minute (a service that is still coming
 * up needs the time). Reuses an in-flight or very recent probe, so asking is
 * nearly always free.
 */
export async function classifyConnection(): Promise<Reachability> {
  if (lastVerdict && Date.now() - lastVerdict.at < VERDICT_TTL_MS) {
    return lastVerdict.value;
  }
  return probe();
}

/**
 * A request came back with a status we could read and it was not a 5xx — the
 * connection works AND the services are usable.
 */
export function reportRequestSuccess(): void {
  markReachable();
}

/**
 * A request came back with a readable 5xx. No probe needed: this IS the proof
 * that the host is up and the service is not, so the bar goes up immediately
 * rather than after two failures and a round trip.
 */
export function reportServiceUnavailable(): void {
  consecutiveFailures += 1;
  lastVerdict = { value: 'unavailable', at: Date.now() };
  markUnavailable();
}

/**
 * A request failed at the transport layer: no response at all, or a timeout.
 * One of these is usually just a blip, so confirm with a probe before
 * declaring the connection down.
 */
export function reportRequestFailure(): void {
  consecutiveFailures += 1;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    markLost();
    return;
  }
  // Probe on the FIRST failure, not the second. The retry ladder needs a verdict
  // to pace itself by, and waiting for a second failure meant the decision was
  // always made blind — which is how a 503 ended up on the 1s/3s/8s ladder meant
  // for stale sockets. The probe is deduped and cached, so this is not extra
  // traffic per failure; it just arrives in time to be useful.
  void probe();
}

/**
 * A request has been in flight far too long. It may be a legitimately slow
 * endpoint (document processing, service power-on) — the probe is what tells
 * the two apart, so a slow-but-fine request never raises the bar.
 */
export function reportRequestStalled(): void {
  void probe();
}

/** Manual "check again", for the retry button on the bar. */
export function checkConnectionNow(): void {
  void probe();
}

// The browser's own signals are still worth having: `offline` is a reliable
// negative (a link really did drop), and `online` / a tab regaining focus are
// the moments recovery is most likely — exactly when a probe is worth spending.
if (typeof window !== 'undefined') {
  // Already offline when the app loaded — say so from the start rather than
  // waiting for the first request to fail.
  //
  // Goes through markLost() rather than assigning `state` directly, which used to
  // leave the module in 'lost' without ever having published it. That mattered
  // once the activity log arrived: the later probe failure saw the state already
  // down, so no outage window was ever opened and a session that started offline
  // produced no `connection_lost` entry at all. Subscribers are unaffected —
  // subscribeConnection replays the current state on subscribe.
  if (browserOffline()) {
    markLost();
  }
  // markLost() reads navigator.onLine itself, so this lands on 'offline' — and a
  // link that drops mid-outage re-words the bar from "can't reach the server" to
  // "you're offline".
  window.addEventListener('offline', () => markLost());
  // Not a bare probe: the link being back does not mean it works yet.
  window.addEventListener('online', () => void probeAfterReconnect());
  window.addEventListener('focus', () => {
    if (isDown(state)) void probe();
  });
  document.addEventListener('visibilitychange', () => {
    // Waking from sleep / returning to a background tab is precisely when the
    // socket pool is stale, so re-check rather than wait for the user's next
    // click to fail.
    if (document.visibilityState === 'visible' && isDown(state)) void probe();
  });
}
